import React, { MouseEventHandler, forwardRef, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import isEmpty from 'lodash/isEmpty';
import { useSelector } from 'react-redux';
import requestGetHeroPlannerSchedulePushContent from '~/queries/customerCommunication/requestGetHeroPlannerSchedulePushContent';

import { Alert, AlertTitle, Autocomplete, Box, Button, Stack, TextField } from '@mui/material';

import { MAX_CHAR_LENGTH } from '~/consts/customerCommunication/inputErrors';
import { RequestStatus } from '~/consts/requestConstants';

import { isRequestRejected } from '~/utils/requestUtils';
import { getCharacterCount, getWordCount } from '~/utils/stringUtils';

import { getWarningColourTheme, isInvalidInput, isValidUrl } from '../../utils/validationUtils';

import SchedulePushImageInput from './Inputs/SchedulePushImageInput';

export enum SCHEDULE_PUSH_CONTENT_INPUT_NAMES {
  TITLE = 'schedule_push_content_title_input_name',
  SUBTITLE = 'schedule_push_content_subtitle_input_name',
  MESSAGE = 'schedule_push_content_message_input_name',
  IMAGE_ID = 'schedule_push_content_image_id_input_name',
  LINK = 'schedule_push_content_link_input_name',
}

export enum SCHEDULE_PUSH_TYPE {
  DEFAULT_PUSH = 'DEFAULT',
  EVENING_PUSH = 'EVENING',
}

export function parseSchedulePushContentFormData(
  formData: FormData,
  scheduleType: SCHEDULE_PUSH_TYPE | undefined = undefined,
) {
  const pushTitle = String(formData.get(SCHEDULE_PUSH_CONTENT_INPUT_NAMES.TITLE));
  const pushSubtitle = String(formData.get(SCHEDULE_PUSH_CONTENT_INPUT_NAMES.SUBTITLE));
  const pushMessage = String(formData.get(SCHEDULE_PUSH_CONTENT_INPUT_NAMES.MESSAGE));
  const pushLink = String(formData.get(SCHEDULE_PUSH_CONTENT_INPUT_NAMES.LINK));
  const pushSvcImageId = String(formData.get(SCHEDULE_PUSH_CONTENT_INPUT_NAMES.IMAGE_ID));

  return {
    pushTitle: !isEmpty(pushTitle) ? pushTitle : undefined,
    pushSubtitle: !isEmpty(pushSubtitle) ? pushSubtitle : undefined,
    pushMessage: !isEmpty(pushMessage) ? pushMessage : undefined,
    pushLink: !isEmpty(pushLink) ? pushLink : undefined,
    pushSvcImageId: !isEmpty(pushSvcImageId) ? pushSvcImageId : undefined,
    scheduleType: !isEmpty(scheduleType) ? scheduleType : undefined,
  };
}

interface Props {
  titleDefaultValue: string;
  subtitleDefaultValue: string;
  messageDefaultValue: string;
  linkDefaultValue: string;
  imageIdDefaultValue: string;
  countryGroupId?: string;
  countryId?: string;
  offerIds?: Array<string>;
  showPrefillButton?: boolean;
}

const SchedulePushContentForm = forwardRef<HTMLFormElement, Props>((props, ref) => {
  const {
    titleDefaultValue,
    subtitleDefaultValue,
    messageDefaultValue,
    linkDefaultValue,
    imageIdDefaultValue,
    countryGroupId,
    countryId,
    offerIds = [],
    showPrefillButton = true,
  } = props;

  const brand = useSelector((state: App.State) => state.tenant.brand);

  const titleInputRef = useRef<HTMLInputElement>(null);
  const subtitleInputRef = useRef<HTMLInputElement>(null);
  const messageInputRef = useRef<HTMLInputElement>(null);
  const imageIdInputRef = useRef<HTMLInputElement>(null);
  const linkInputRef = useRef<HTMLInputElement>(null);

  const [pushOfferId, setPushOfferId] = useState(offerIds[0] ?? '');

  useEffect(() => {
    setPushOfferId((current) => current || offerIds[0] || '');
  }, [offerIds]);

  const [emailContentRequest, setEmailContentRequest] = useState<
    Utils.RequestState<CustomerCommunication.HeroPlannerSchedulePushContent, string>
  >({
    status: RequestStatus.INITIAL,
  });

  const params = useMemo(() => {
    if (brand && countryGroupId && countryId && pushOfferId) {
      return [brand, countryGroupId, countryId, pushOfferId] as const;
    }
  }, [brand, countryGroupId, countryId, pushOfferId]);

  const handlePrefillClick = useCallback<MouseEventHandler<HTMLButtonElement>>(
    async (event) => {
      event.preventDefault();
      setEmailContentRequest({
        status: RequestStatus.PENDING,
        params,
      });

      try {
        const result = await requestGetHeroPlannerSchedulePushContent(...params);
        setEmailContentRequest({
          status: RequestStatus.FULFILLED,
          params,
          result,
        });
      } catch (error) {
        setEmailContentRequest({
          status: RequestStatus.REJECTED,
          params,
          error,
        });
      }
    },
    [params],
  );

  useEffect(() => {
    if (emailContentRequest.result?.title) {
      setTitle(emailContentRequest.result.title);
    }
    if (emailContentRequest.result?.subtitle) {
      setSubtitle(emailContentRequest.result.subtitle);
    }
    if (emailContentRequest.result?.message) {
      setMessage(emailContentRequest.result.message);
    }
    if (emailContentRequest.result?.imageId) {
      imageIdInputRef.current.value = emailContentRequest.result.imageId;
    }
    if (emailContentRequest.result?.link) {
      setLink(emailContentRequest.result.link);
    }
  }, [emailContentRequest.result]);

  const [title, setTitle] = useState(titleDefaultValue);
  const [subtitle, setSubtitle] = useState(subtitleDefaultValue);
  const [message, setMessage] = useState(messageDefaultValue);
  const [link, setLink] = useState(linkDefaultValue);

  return (
    <Stack component="form" ref={ref} direction="column" gap={2}>
      {showPrefillButton && (
        <>
          <Autocomplete
            freeSolo
            value={pushOfferId}
            onChange={(_event, newValue) => setPushOfferId(newValue ?? '')}
            onInputChange={(_event, newInputValue) => setPushOfferId(newInputValue)}
            options={offerIds}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Push Offer ID"
                variant="standard"
                InputLabelProps={{ shrink: true }}
                helperText="Select a hero offer or enter a custom offer ID to generate push content from."
                placeholder="e.g. 00000000-0000-0000-0000-000000000000"
              />
            )}
          />
          <Box>
            <Button variant="outlined" color="secondary" disabled={!params} onClick={handlePrefillClick}>
              Prefill push content
            </Button>
            {isRequestRejected(emailContentRequest) && (
              <Alert severity="error" sx={{ mt: 1 }}>
                <AlertTitle>Problem with prefilling!</AlertTitle>
                <pre>{emailContentRequest.error}</pre>
              </Alert>
            )}
          </Box>
        </>
      )}
      <TextField
        inputRef={titleInputRef}
        name={SCHEDULE_PUSH_CONTENT_INPUT_NAMES.TITLE}
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        label="Title"
        variant="standard"
        multiline
        helperText={
          isInvalidInput(title) ? 'Invalid input' : `Chars: ${getCharacterCount(title)} Words: ${getWordCount(title)}`
        }
        error={isInvalidInput(title)}
        InputLabelProps={{ shrink: true }}
        slotProps={{
          formHelperText: {
            sx: {
              color: getWarningColourTheme(title, MAX_CHAR_LENGTH.TITLE),
            },
          },
        }}
      />
      <TextField
        inputRef={subtitleInputRef}
        name={SCHEDULE_PUSH_CONTENT_INPUT_NAMES.SUBTITLE}
        value={subtitle}
        onChange={(e) => setSubtitle(e.target.value)}
        label="Subtitle"
        variant="standard"
        multiline
        InputLabelProps={{ shrink: true }}
        helperText={
          isInvalidInput(subtitle)
            ? 'Invalid input'
            : `Chars: ${getCharacterCount(subtitle)} Words: ${getWordCount(subtitle)}`
        }
        error={isInvalidInput(subtitle)}
        slotProps={{
          formHelperText: {
            sx: {
              color: getWarningColourTheme(subtitle, MAX_CHAR_LENGTH.SUBTITLE),
            },
          },
        }}
      />
      <TextField
        inputRef={messageInputRef}
        name={SCHEDULE_PUSH_CONTENT_INPUT_NAMES.MESSAGE}
        value={message}
        onChange={(e) => setMessage(e.target.value)}
        label="Message"
        variant="standard"
        multiline
        InputLabelProps={{ shrink: true }}
        helperText={
          isInvalidInput(message)
            ? 'Invalid input'
            : `Chars: ${getCharacterCount(message)} Words: ${getWordCount(message)}`
        }
        error={isInvalidInput(message)}
        slotProps={{
          formHelperText: {
            sx: {
              color: getWarningColourTheme(message, MAX_CHAR_LENGTH.MESSAGE),
            },
          },
        }}
      />
      <TextField
        inputRef={linkInputRef}
        name={SCHEDULE_PUSH_CONTENT_INPUT_NAMES.LINK}
        value={link}
        onChange={(e) => setLink(e.target.value)}
        label="Link"
        variant="standard"
        InputLabelProps={{ shrink: true }}
        helperText={!isValidUrl(brand, link) ? 'Invalid URL' : ''}
        error={!isValidUrl(brand, link)}
      />
      <SchedulePushImageInput
        ref={imageIdInputRef}
        name={SCHEDULE_PUSH_CONTENT_INPUT_NAMES.IMAGE_ID}
        defaultValue={imageIdDefaultValue}
      />
    </Stack>
  );
});

SchedulePushContentForm.displayName = 'SchedulePushForm';
export default SchedulePushContentForm;
